import { useCallback, useEffect } from "react";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { publish } from "@Libs/Events/services/events";
import { IUser } from "@modules/Users/services/users";
import { useDeviceType } from "@UI/hooks/useDeviceType";
import { IRecordEvent, IRecordEventList } from "../types/event.types";
import { useMetadata } from "./useMetadata";

export interface IUseRecordEvent<T extends keyof IRecordEventList> {
  key: T;
  payload?: { data?: Partial<IRecordEvent<T>["payload"]["data"]> } & Partial<
    IRecordEvent<T>["payload"]
  >;
}

const actorTypeFor = (user: IUser) => {
  return user ? (user.admin ? "admin" : "user") : "guest";
};

export const useRecordEvent = (
  { key, payload = {} }: IUseRecordEvent<keyof IRecordEventList>,
  { triggerOnMount = true } = {}
) => {
  const { currentUser, isSuccess: isCurrentUserLoaded } = useCurrentUser();
  const deviceType = useDeviceType();
  const {
    metadata: { context },
  } = useMetadata();

  const recordEvent = useCallback(
    (overrides?: Partial<IRecordEvent<keyof IRecordEventList>["payload"]>) => {
      if (!currentUser) return;

      const { data, metadata } = payload;

      const event: IRecordEvent<keyof IRecordEventList> = {
        key,
        payload: {
          metadata: {
            system: "web",
            actorId: currentUser?.id,
            actorType: actorTypeFor(currentUser),
            userAgent: navigator.userAgent,
            deviceType,
            context,
            ...metadata,
            ...overrides?.metadata,
          },
          data: {
            ...data,
            ...overrides?.data,
          },
        },
      };

      try {
        publish(event);
      } catch (error) {
        console.log({ error });
      }
    },
    [currentUser, context, deviceType, key, payload]
  );

  useEffect(() => {
    if (triggerOnMount) recordEvent();
  }, [currentUser?.id]);

  return { recordEvent, isCurrentUserLoaded };
};
